import { RefObject, useEffect, useState, useRef } from "react";
import { Input } from "reactstrap";
import { useDebouncedCallback } from "use-debounce";
import { CyDelayedFilterInput } from "./CyDelayedFilterInput";

interface Props {
  onSearchTermChanged: (searchTerm: string) => void;
  initialValue?: string;
  autoFocus: boolean;
  inputRef: RefObject<HTMLInputElement>;
  placeholder: string;
}

export function DelayedFilterInput({
  onSearchTermChanged,
  initialValue = "",
  autoFocus,
  inputRef,
  placeholder,
}: Props) {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const lastReportedValueRef = useRef(initialValue);

  const debouncedOnSearchTermChanged = useDebouncedCallback((val: string) => {
    lastReportedValueRef.current = val;
    onSearchTermChanged(val);
  }, 300);

  // Update internal state if initialValue changes from outside
  useEffect(() => {
    if (initialValue !== lastReportedValueRef.current) {
      setSearchTerm(initialValue);
      // Important: cancel any pending updates from the local state when the source of truth changes
      debouncedOnSearchTermChanged.cancel();
      lastReportedValueRef.current = initialValue;
    }
  }, [initialValue, debouncedOnSearchTermChanged]);

  const handleFlush = () => {
    debouncedOnSearchTermChanged.flush();
  };

  return (
    <Input
      data-cy={CyDelayedFilterInput.InputBoxSearchSong}
      type="search"
      innerRef={inputRef}
      placeholder={placeholder}
      autoFocus={autoFocus}
      value={searchTerm}
      onBlur={handleFlush}
      onKeyDown={(e) => e.key === "Enter" && handleFlush()}
      onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
        const newVal = ev.currentTarget.value;
        setSearchTerm(newVal);
        debouncedOnSearchTermChanged(newVal);
      }}
    />
  );
}
